import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import {
  useUnsubscribeFromAllEmails,
  useUnsubscribeFromSatisfactionSurveys,
} from '@/hooks/usePatientSatisfactionSurvey';

type UnsubscribeState = 'idle' | 'surveys' | 'allEmails';

const onError = () => {
  enqueueSnackbar(
    'Une erreur est survenue lors de la mise à jour des préférences de communication, veuillez nous excuser pour le désagrément.',
    { variant: 'error', autoHideDuration: 5000 },
  );
};

export const PatientSatisfactionUnsubscribePage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [state, setState] = useState<UnsubscribeState>('idle');
  const {
    mutate: unsubscribeFromSatisfactionSurveys,
    isLoading: isUnsubscribingFromSurveys,
  } = useUnsubscribeFromSatisfactionSurveys(id);
  const { mutate: unsubscribeFromAllEmails, isLoading: isUnsubscribingAll } =
    useUnsubscribeFromAllEmails(id);
  const isLoading = isUnsubscribingFromSurveys || isUnsubscribingAll;

  const allEmailsLink = (
    <button
      type="button"
      className="font-light text-gray-600 underline disabled:opacity-50"
      disabled={isLoading}
      onClick={() =>
        unsubscribeFromAllEmails(undefined, {
          onSuccess: () => setState('allEmails'),
          onError,
        })
      }
    >
      Se désinscrire de tous les emails
    </button>
  );

  if (state !== 'idle') {
    return (
      <div className="flex h-full flex-col px-4 n2xs:px-6 nsm:px-0">
        <PatientPageLayout
          className="justify-start"
          footerClassName="hidden"
          title={
            <h1 className="text-2xl font-semibold leading-tight min-[23rem]:text-3.5xl nxs:text-center">
              Vos préférences ont bien été enregistrées
            </h1>
          }
          footer={null}
        >
          <div className="flex w-full flex-col items-center gap-6">
            <p className="font-light nxs:text-center">
              {state === 'allEmails'
                ? "Vous ne recevrez plus d'emails de notre part."
                : 'Vous ne recevrez plus de questionnaires de satisfaction.'}
            </p>
            {state === 'surveys' && allEmailsLink}
          </div>
        </PatientPageLayout>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-4 n2xs:px-6 nsm:px-0">
      <PatientPageLayout
        className="justify-start"
        footerClassName="hidden"
        title={
          <h1 className="text-2xl font-semibold leading-tight min-[23rem]:text-3.5xl nxs:text-center">
            Se désinscrire des questionnaires de satisfaction
          </h1>
        }
        footer={null}
      >
        <div className="flex w-full flex-col items-center gap-6">
          <p className="font-light nxs:text-center">
            Confirmez pour ne plus recevoir de questionnaires de satisfaction
            après vos consultations. Les autres emails de vos praticiens ne sont
            pas concernés.
          </p>
          <Button
            variant="ctaPrimary"
            size="lg"
            disabled={isLoading}
            onClick={() =>
              unsubscribeFromSatisfactionSurveys(undefined, {
                onSuccess: () => setState('surveys'),
                onError,
              })
            }
          >
            Confirmer la désinscription
          </Button>
        </div>
      </PatientPageLayout>
    </div>
  );
};
